import React from 'react';
import { useLanguage } from '@/LanguageContext';
import { motion } from 'framer-motion';
import { ShieldCheck, Globe, DollarSign, Headphones, Package, ClipboardCheck } from 'lucide-react';

const WhyChooseUs = () => {
  const { t } = useLanguage();
  const icons = [ShieldCheck, Globe, DollarSign, Headphones, Package, ClipboardCheck];

  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: (i) => ({
      opacity: 1, y: 0,
      transition: { duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
    }),
  };

  const reasons = t('whyUs.reasons');

  return (
    <section id="why-us" data-testid="why-us-section" className="py-24 md:py-32 bg-[#0A1628] relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.65\' numOctaves=\'3\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")' }} />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <motion.p
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0}
          className="text-[#C5A55A] text-sm font-mono tracking-[0.3em] uppercase mb-4"
        >
          {t('whyUs.overline')}
        </motion.p>
        <motion.h2
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1}
          className="font-heading text-4xl md:text-5xl font-semibold tracking-tight text-white mb-16"
          data-testid="why-us-title"
        >
          {t('whyUs.title')}
        </motion.h2>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reasons.map((reason, i) => {
            const Icon = icons[i];
            const isLarge = i === 0 || i === 5;
            return (
              <motion.div
                key={i}
                variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i + 2}
                className={`group p-8 md:p-10 border border-white/10 hover:border-[#C5A55A]/50 bg-white/[0.03] backdrop-blur-sm transition-all duration-500 hover:-translate-y-1 ${
                  isLarge ? 'md:col-span-2 lg:col-span-1' : ''
                }`}
                data-testid={`why-us-card-${i}`}
              >
                <div className="w-14 h-14 flex items-center justify-center border border-[#C5A55A]/30 mb-6 group-hover:bg-[#C5A55A]/10 transition-colors duration-300">
                  <Icon className="w-6 h-6 text-[#C5A55A]" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-white mb-3">{reason.title}</h3>
                <p className="text-slate-400 leading-relaxed text-sm">{reason.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
